import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, tap, throwError } from 'rxjs';
import { ErrorVTO } from '../../pages/admin-dashboard/products/product.model';
import { CompanyLookupResponse, MFGCompanyDTO, MFGCompanyResultSet, MFGCompanyVTO } from './company.model';
import { AuthService } from '../auth-service';

@Injectable({ providedIn: 'root' })
export class MFGCompanyService {
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private API = environment.productsApiUrl;

  getAllCompanies(paramsIn?: {
    companyTitle?: string;
    pageSize?: number;
    pageOffset?: number;
    pageNum?: number;
    orderBy?: 'COMPANY_TITLE' | 'CREATION_DATE';
    orderDir?: 'ASC' | 'DESC';
  }) {
    let params = new HttpParams();
    if (paramsIn) {
      if (paramsIn.companyTitle) {
        params = params.set('companyTitle', paramsIn.companyTitle);
      }
      if (paramsIn.pageSize) {
        params = params.set('pageSize', paramsIn.pageSize.toString());
      }
      if (paramsIn.pageOffset) {
        params = params.set('pageOffset', paramsIn.pageOffset.toString());
      }
      if (paramsIn.pageNum) {
        params = params.set('pageNum', paramsIn.pageNum.toString());
      }
      if (paramsIn.orderBy) {
        params = params.set('orderBy', paramsIn.orderBy);
      }
      if (paramsIn.orderDir) {
        params = params.set('orderDir', paramsIn.orderDir);
      }
    }
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.get<MFGCompanyResultSet>(`${this.API}/companies/mfg`, { params, headers }).pipe(
      tap(() => console.log('MFG companies retrieved successfully')),
      catchError(this.handleError),
    );
  }

  createCompany(dto: MFGCompanyDTO) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.post(`${this.API}/companies/mfg`, dto, { headers }).pipe(
      tap(() => console.log('MFG company created successfully')),
      catchError(this.handleError),
    );
  }

  getCompanyById(companyId: number) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.get<MFGCompanyVTO>(`${this.API}/companies/mfg/${companyId}`, { headers }).pipe(
      tap(() => console.log('MFG company retrieved successfully')),
      catchError(this.handleError),
    );
  }

  updateCompany(companyId: number, dto: MFGCompanyDTO) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.put<void>(`${this.API}/companies/mfg/${companyId}`, dto, { headers }).pipe(
      tap(() => console.log('MFG company updated successfully')),
      catchError(this.handleError),
    );
  }

  deleteCompany(companyId: number) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.delete<void>(`${this.API}/companies/mfg/${companyId}`, { headers }).pipe(
      tap(() => console.log('MFG company deleted successfully')),
      catchError(this.handleError),
    );
  }

  getAllCompaniesLookup() {
    return this.http
      .get<CompanyLookupResponse>(`${this.API}/lookup/companies/mfg`)
      .pipe(catchError((error) => throwError(() => error)));
  }

  private handleError(error: HttpErrorResponse) {
    const apiError: ErrorVTO = error.error;
    console.error('An API error occurred:', apiError);
    return throwError(() => apiError);
  }
}
